"use client";

import { useCallback, useEffect, useRef } from "react";
import { useWallet } from "@aptos-labs/wallet-adapter-react";
import { useConnection } from "@/contexts/connection-context";
import { useAuth } from "@/contexts/auth-context";
import { AuthService } from "@/services/auth.service";

export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { connected, account } = useWallet();
  const { isAuthenticated, setIsAuthenticated, connectWallet } = useConnection();
  const { accessToken, setTokens, clearTokens } = useAuth();
  const hasInitializedRef = useRef(false);

  const getExpiresIn = useCallback((exp?: number) => {
    if (!exp) return undefined;
    return Math.max(Math.floor(exp - Date.now() / 1000), 0);
  }, []);

  const clearSession = useCallback(() => {
    setIsAuthenticated(false);
    clearTokens();
  }, [clearTokens, setIsAuthenticated]);

  const hydrateValidatedToken = useCallback(async (token: string) => {
    const validationResult = await AuthService.validateToken(token);

    if (!validationResult.valid || !validationResult.decoded) {
      return false;
    }

    const validatedToken = validationResult.token || token;
    setTokens(validatedToken, getExpiresIn(validationResult.decoded.exp));
    setIsAuthenticated(true);
    connectWallet(validationResult.decoded.address);
    return true;
  }, [connectWallet, getExpiresIn, setIsAuthenticated, setTokens]);

  // Check authentication status immediately on app load
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    let isCancelled = false;

    const initializeAuth = async () => {
      try {
        if (accessToken) {
          const didHydrateToken = await hydrateValidatedToken(accessToken);
          if (isCancelled) return;
          if (didHydrateToken) return;
        }

        // Check if we have a valid session cookie
        const authStatus = await AuthService.checkAuthStatus();
        if (isCancelled) return;

        const token = authStatus.verifyOutput?.token;
        const address = authStatus.verifyOutput?.user?.address;

        if (authStatus.isAuthenticated && token && address) {
          setTokens(token);
          setIsAuthenticated(true);

          // If we have a valid API session, ensure address state is available
          // even before the wallet adapter finishes reconnecting.
          connectWallet(address);
        } else {
          clearSession();
        }
      } catch (error) {
        console.error("Failed to check auth status:", error);
        if (!isCancelled) {
          clearSession();
        }
      }
    };

    initializeAuth();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, clearSession, connectWallet, hydrateValidatedToken, setIsAuthenticated, setTokens]);

  // Secondary effect to sync wallet connection with auth state
  useEffect(() => {
    // Only run this if we've already checked auth status initially
    if (!isAuthenticated) return;

    const syncWalletState = async () => {
      if (connected && account) {
        // Wallet just connected, verify it matches our session
        const authStatus = await AuthService.checkAuthStatus();

        if (authStatus.isAuthenticated && authStatus.verifyOutput?.user?.address) {
          const sessionAddress = authStatus.verifyOutput.user.address.toLowerCase();
          const connectedAddress = account.address.toString().toLowerCase();

          if (sessionAddress === connectedAddress) {
            if (authStatus.verifyOutput.token) {
              setTokens(authStatus.verifyOutput.token);
            }
            // Same wallet as authenticated session
            connectWallet(authStatus.verifyOutput.user.address);
          } else {
            // Different wallet, need to re-authenticate
            clearSession();
          }
        }
      }
    };

    syncWalletState();
  }, [connected, account, isAuthenticated, clearSession, connectWallet, setTokens]);

  return <>{children}</>;
}
